import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '../../apiClient'
import { CHANGE_LOG_BY_PROGRAM_ID_KEY } from '../../changelog/useChangelog'
import { TRAINING_QUERY_KEYS } from '../../training-modules/trainingModuleApi'
import { CONFIG_ALERTS_QUERY_KEY } from '../config-alerts/useConfigAlerts'

export const REQUALIFICATION_WINDOWS = ['hour', 'day', 'week', 'month'] as const
export type RequalificationWindow = (typeof REQUALIFICATION_WINDOWS)[number]

export type RequalificationTrigger = {
  threshold: number
  window: RequalificationWindow
  effectiveFrom?: string
}

export type RequalificationDataset = {
  enabled: boolean
  engagementCount: number | null
  coverageEnabled: boolean
  dateRange: { start: string | null; end: string | null; movingDayCount: number | null }
  accuracyMin: number | null
  accuracyMax: number | null
}

export type RequalificationDisabled = {
  at: string
  reason: string | null
  by: { id: string; name: string } | null
  threshold: number
  window: RequalificationWindow
}

export type RequalificationAnalytics = {
  msRequalified: number
  avgAttemptsToPass: number | null
  passRate: number | null
  reTriggerRate: number | null
}

export type RequalificationModuleConfig = {
  moduleId: string | null
  pipelineJobId: string | null
  trigger: RequalificationTrigger | null
  disabled: RequalificationDisabled | null
  attempts: { maxAttempts: number | null }
  targetAccuracy: number | null
  targetResolution: number | null
  detailedFeedback: boolean
  instructions: { mediaId: string | null }
  dataset: RequalificationDataset | null
  warnings: string[]
  analytics: RequalificationAnalytics | null
}

export type SaveRequalificationModuleRequest = Omit<
  RequalificationModuleConfig,
  'warnings' | 'analytics' | 'disabled' | 'trigger'
> & { trigger: RequalificationTrigger; enable: boolean }

export type DisableRequalificationModuleRequest = { reason: string }

export const REQUALIFICATION_MODULE_KEY = 'program-requalification-module'

const requalificationUrl = (liveProgramId?: string) =>
  `/int/v3/programs/${liveProgramId}/requalification-module`

export function useGetRequalificationModule(liveProgramId?: string, { enabled = true } = {}) {
  return useQuery<RequalificationModuleConfig>({
    queryKey: [REQUALIFICATION_MODULE_KEY, liveProgramId],
    queryFn: async () => {
      const { data } = await apiClient.get<RequalificationModuleConfig>(requalificationUrl(liveProgramId))
      return data
    },
    enabled: Boolean(liveProgramId) && enabled,
  })
}

export function useSaveRequalificationModule(liveProgramId?: string) {
  const queryClient = useQueryClient()

  return useMutation<RequalificationModuleConfig, unknown, SaveRequalificationModuleRequest>({
    retry: false,
    mutationFn: async (payload) => {
      if (!liveProgramId) throw new Error('Live program is not available')
      const { data } = await apiClient.put<RequalificationModuleConfig>(
        requalificationUrl(liveProgramId),
        payload
      )
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData([REQUALIFICATION_MODULE_KEY, liveProgramId], data)
      void queryClient.invalidateQueries({ queryKey: [CHANGE_LOG_BY_PROGRAM_ID_KEY, liveProgramId] })
      void queryClient.invalidateQueries({ queryKey: [TRAINING_QUERY_KEYS.configuration] })
      void queryClient.invalidateQueries({ queryKey: [CONFIG_ALERTS_QUERY_KEY] })
    },
  })
}

export function useDisableRequalificationModule(liveProgramId?: string) {
  const queryClient = useQueryClient()

  return useMutation<RequalificationModuleConfig, unknown, DisableRequalificationModuleRequest>({
    retry: false,
    mutationFn: async (body) => {
      if (!liveProgramId) throw new Error('Live program is not available')
      const { data } = await apiClient.post<RequalificationModuleConfig>(
        `${requalificationUrl(liveProgramId)}/disable`,
        body
      )
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData([REQUALIFICATION_MODULE_KEY, liveProgramId], data)
      void queryClient.invalidateQueries({ queryKey: [CHANGE_LOG_BY_PROGRAM_ID_KEY, liveProgramId] })
      void queryClient.invalidateQueries({ queryKey: [CONFIG_ALERTS_QUERY_KEY] })
    },
  })
}
